import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { SearchSelect } from '@doccareplus/ui';
import { citiesForCountry, countryOptions, statesForCountry } from '../geo/geoReference';

export interface LocationValue {
  /** ISO 3166-1 alpha-2 country code. */
  country: string;
  /** ISO 3166-2 state code (state isoCode from country-state-city). */
  stateCode: string;
  /** City name. */
  city: string;
}

export interface LocationSelectProps {
  value: LocationValue;
  onChange: (value: LocationValue) => void;
  /** Lock the Country select (it mirrors another source, e.g. the clinic phone country). */
  disableCountry?: boolean;
  /** Extra field rendered in the grid cell beside City (e.g. postal code). */
  trailing?: ReactNode;
  labels: {
    country: string;
    state: string;
    city: string;
  };
  placeholders: {
    country: string;
    state: string;
    city: string;
  };
  searchPlaceholders: {
    country: string;
    state: string;
    city: string;
  };
  noResultsLabels: {
    country: string;
    state: string;
    city: string;
  };
}

/**
 * LocationSelect — three cascading selects (Country → State → City) backed by
 * compact generated reference data. City names are fetched only for the selected country.
 * matching the backend clinic-profile contract.
 *
 * Pure presentational: it owns no business rules and forwards a full
 * LocationValue back to the caller on every change.
 */
export function LocationSelect({ value, onChange, disableCountry, trailing, labels, placeholders, searchPlaceholders, noResultsLabels }: LocationSelectProps) {
  const states = useMemo(
    () => statesForCountry(value.country),
    [value.country]
  );
  const [loadedCities, setLoadedCities] = useState<{
    country: string;
    stateCode: string;
    names: readonly string[];
  }>({ country: '', stateCode: '', names: [] });
  const cities = loadedCities.country === value.country && loadedCities.stateCode === value.stateCode
    ? loadedCities.names
    : [];
  useEffect(() => {
    let current = true;
    if (value.country && value.stateCode) {
      void citiesForCountry(value.country).then((byState) => {
        if (current) setLoadedCities({
          country: value.country,
          stateCode: value.stateCode,
          names: byState[value.stateCode] ?? [],
        });
      }).catch(() => { /* The select remains empty and the profile can be retried. */ });
    }
    return () => { current = false; };
  }, [value.country, value.stateCode]);

  const handleCountry = (country: string) => {
    // Changing country resets state + city.
    onChange({ country, stateCode: '', city: '' });
  };

  const handleState = (stateCode: string) => {
    // Changing state resets city.
    onChange({ ...value, stateCode, city: '' });
  };

  const handleCity = (city: string) => {
    onChange({ ...value, city });
  };

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <SearchSelect
        label={labels.country}
        appearance="input"
        requiredMark
        disabled={disableCountry}
        value={value.country || null}
        onChange={handleCountry}
        placeholder={placeholders.country}
        options={countryOptions.map((country) => ({
          value: country.code,
          label: country.name,
        }))}
        searchPlaceholder={searchPlaceholders.country}
        noResultsLabel={noResultsLabels.country}
      />

      <SearchSelect
        label={labels.state}
        appearance="input"
        requiredMark
        value={value.stateCode || null}
        disabled={!value.country}
        onChange={handleState}
        placeholder={placeholders.state}
        options={states.map((state) => ({
          value: state.code,
          label: state.name,
        }))}
        searchPlaceholder={searchPlaceholders.state}
        noResultsLabel={noResultsLabels.state}
      />

      <SearchSelect
        label={labels.city}
        appearance="input"
        requiredMark
        value={value.city || null}
        disabled={!value.stateCode}
        onChange={handleCity}
        placeholder={placeholders.city}
        options={cities.map((city) => ({
          value: city,
          label: city,
        }))}
        searchPlaceholder={searchPlaceholders.city}
        noResultsLabel={noResultsLabels.city}
      />

      {trailing}
    </div>
  );
}
